import axios from 'axios';
import store from "@/store";

const UNAUTHORIZED_HINTS = [
    '401',
    'unauthorized',
    'nao autorizado',
    'não autorizado',
    'invalid token',
    'token invalido',
    'token inválido',
    'invalid session',
    'sessao invalida',
    'sessão inválida',
    'dados invalidos',
    'dados inválidos'
];

function normalizeText(value) {
    return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
}

function hasUnauthorizedHint(value) {
    const text = normalizeText(value);
    return UNAUTHORIZED_HINTS.some((hint) => text.includes(normalizeText(hint)));
}

function hasUnauthorizedError(errors) {
    if (Array.isArray(errors)) {
        return errors.some(hasUnauthorizedError);
    }

    if (errors && typeof errors === 'object') {
        return Object.values(errors).some(hasUnauthorizedError);
    }

    if (typeof errors === 'number') {
        return errors === 401;
    }

    if (typeof errors === 'string') {
        return hasUnauthorizedHint(errors);
    }

    return false;
}

function isInvalidSessionPayload(payload) {
    const candidates = [payload, payload?.data, payload?.data?.data].filter(Boolean);

    return candidates.some((candidate) => {
        if (!candidate || typeof candidate !== 'object') {
            return false;
        }

        if (String(candidate.state || '').toLowerCase() !== 'nok') {
            return false;
        }

        return hasUnauthorizedError(candidate.errors)
            || hasUnauthorizedHint(candidate.message)
            || hasUnauthorizedHint(candidate.msg)
            || hasUnauthorizedHint(candidate.error);
    });
}

async function forceReauthentication() {
    if (store.getters.showSessionExpiredModal) {
        return;
    }

    store.commit('SHOW_MAINTENANCE_MODAL', false);
    store.commit('SHOW_DEVICE_INCOMPATIBILITY_MODAL', false);
    store.commit('SET_DEVICE_INCOMPATIBILITY_APP_TYPE', null);
    store.commit('setAutoLogout');
    store.commit('setIsAuthorized', false);
    store.commit('setIsAuthenticated', false);
    store.commit('setUserSession', { app_token: null, expirationDate: null });
    store.commit('setShowSessionExpiredModal', true);
    localStorage.clear();
}

const myAxios = axios.create({
    baseURL: 'https://geoops.meo.pt/app',
    timeout: 10000,
});

myAxios.interceptors.request.use(
    config => {
        if (!navigator.onLine) {
            store.commit('setOnline', { online: false });
            return Promise.reject(new Error('Sem conexão com a Internet. Verifique sua rede.'));
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

myAxios.interceptors.response.use(
    async response => {
        if (navigator.onLine && response.status === 200) {
            store.commit('setOnline', { online: true });
        }

        if (isInvalidSessionPayload(response.data)) {
            await forceReauthentication();
            return Promise.reject(new Error('Início de Nova Sessão Detetado! Necessário Efetuar Novo Login!'));
        }

        return response;
    },
    async error => {
        const isOffline = !navigator.onLine;
        const isTimeout = error.code === 'ECONNABORTED';
        const isAxiosNetworkError = error.code === 'ERR_NETWORK' || (!error.response && !!error.request);

        if (isOffline) {
            store.commit('setOnline', { online: false });
            return Promise.reject(new Error('Sem conexão com a Internet. Verifique sua rede.'));
        }

        if (isTimeout) {
            store.commit('setOnline', { online: true });
            return Promise.reject(new Error('Tempo de resposta excedido. Tente novamente.'));
        }

        if (isAxiosNetworkError) {
            store.commit('setOnline', { online: true });
            return Promise.reject(new Error('Não foi possível receber a resposta da API (CORS/SSL/Proxy/Rede).'));
        }

        if (error.response) {
            const status = error.response.status;

            if (status === 401) {
                store.commit('setOnline', { online: true });
                await forceReauthentication();
                return Promise.reject(new Error('Início de Nova Sessão Detetado! Necessário Efetuar Novo Login!'));
            }

            if (status === 500) {
                store.commit('setOnline', { online: true });
                return Promise.reject(new Error('Erro interno do servidor. Tente novamente mais tarde.'));
            }

            return Promise.reject(error.response.data || new Error('Ocorreu um erro ao processar a solicitação.'));
        }

        return Promise.reject(new Error(error.message || 'Erro desconhecido.'));
    }
);

export default myAxios;